import React from "react";
import {useTranslation} from "react-i18next";
import CustomCard from "../../../../components/CustomCard";
import CustomTooltip from "../../../../components/CustomTooltip";
import CustomChip from "../../../../components/CustomChip";
import {CustomCheckbox} from "../../../../components/CustomCheckbox";
import {
    IconAreaCircle,
    IconCheckboxChecked,
    IconCheckboxUnchecked,
    IconCloseChip,
    IconZoneDevices,
    PlusIconsOptions
} from "../../../../components/buttons/IconButtons";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../enums/statusColorEnum";
import {status} from "../../../../enums/statusEnum";

/**
 * Card of a zone in the available, the selected or the assigned list of a zone access: its name and
 * status, and below a separator the devices it has. An available zone is added with the plus icon,
 * a selected or assigned one is removed with the close icon, and an assigned one can be checked to
 * remove several at once.
 *
 * @param {Object} item Zone, with its id, name, statusId and devicesCount.
 * @param {boolean} disabled Whether the zone is already selected, only for the available list.
 * @param {Function} onAdd Called with the zone to add (available list).
 * @param {Function} onDelete Called with the zone to remove (selected or assigned list).
 * @param {boolean} checked Whether the zone is checked (assigned list).
 * @param {Function} onCheck Called with the zone and whether it is checked (assigned list).
 */
const ZoneItemCard = ({item, disabled = false, onAdd, onDelete, checked = false, onCheck}) => {
    const {t} = useTranslation();

    return (
        <CustomCard
            classes="w-100"
            classCard="card-border-8px"
            classContent={`background-shades-50 ${onAdd ? (disabled ? 'disable-card-item' : 'background-hover-shades-100') : ''}`}
        >
            <div className="d-flex align-items-center">
                {onCheck &&
                    <div className="mr-2">
                        <CustomCheckbox
                            id={`cb_zic_row_${item?.id}`}
                            checked={checked}
                            onChange={(e) => onCheck(item, e.target.checked)}
                            icon={<IconCheckboxUnchecked className={"icon-color-general-950"}/>}
                            checkedIcon={<IconCheckboxChecked className={"icon-color-shades-800"}/>}
                            disableRipple={true}
                        />
                    </div>
                }
                <div className="flex-grow-1 minw-0">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center flex-grow-1 minw-0">
                            <div><IconAreaCircle className="mr-8px icon-color-general-950"/></div>
                            <div className="flex-grow-1 minw-0">
                                <CustomTooltip
                                    text={item?.name}
                                    classes="fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis-1-row"
                                    showIfTruncated={true}
                                />
                            </div>
                        </div>
                        {item?.statusId &&
                            <div className="ml-3">
                                <CustomChip
                                    background={statusColor[item.statusId]}
                                    fontColor={statusFontColor[item.statusId]}
                                    borderColor={statusBorderColor[item.statusId]}
                                    legend={t(status[item.statusId])}
                                    variant={"large"}
                                />
                            </div>
                        }
                    </div>

                    <div className="border-bottom-shades-300 my-12px"/>

                    <div className="d-flex align-items-center">
                        <div><IconZoneDevices className="mr-8px"/></div>
                        <span className="fs-14px font-color-general-950 ibl-font-w-300">
                            {`${item?.devicesCount ?? 0} ${t('devices')}`}
                        </span>
                    </div>
                </div>
                {onAdd &&
                    <PlusIconsOptions
                        className="plus-icons-options icon-color-shades-400 ml-3"
                        onClick={() => onAdd(item)}
                    />
                }
                {onDelete &&
                    <IconCloseChip
                        className="icon-color-shades-400 icon-hover-shades-950 ml-3"
                        onClick={() => onDelete(item)}
                    />
                }
            </div>
        </CustomCard>
    );
};

export default ZoneItemCard;
